import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import {
  historicalAccidentApi,
  historicalActivityApi,
  historicalPollutionApi,
  historicalProductApi,
  historicalRestrictionApi,
  historicalICPEApi,
  historicalBombingsApi,
  testimonyApi,
} from "@/api/projectClient";
import _ from "lodash";
import {
  zoneApi,
  componentApi,
  buildingApi,
  outdoorSpaceApi,
  basiasBasolSisNearbySiteOtherApi,
  spreadingApi,
} from "@/api/cartographyClient";
import Testimony from "@/models/testimonials/Testimony";
import { metadataToParams, filtersToParams } from "@/utils/restMetadata";
import { useMissionStore } from "@/store/modules/mission"; // Assuming you have a mission store
import {
  Accident,
  Activity,
  ICPE,
  Pollution,
  Product,
  Restriction,
  Bombings,
} from "@/models/testimonials/HistoricalItem.js";
import { validate as isUuid } from "uuid";

const excludeFields = ["createdAt", "updatedAt"];

const HISTORICAL = {
  accident: {
    api: historicalAccidentApi,
    className: "HistoricalAccidentApi",
    createKey: "accidentRequest",
    patchKey: "patchedAccidentRequest",
  },
  activity: {
    api: historicalActivityApi,
    className: "HistoricalActivityApi",
    createKey: "activityRequest",
    patchKey: "patchedActivityRequest",
  },
  pollution: {
    api: historicalPollutionApi,
    className: "HistoricalPollutionApi",
    createKey: "pollutionRequest",
    patchKey: "patchedPollutionRequest",
  },
  product: {
    api: historicalProductApi,
    className: "HistoricalProductApi",
    createKey: "productRequest",
    patchKey: "patchedProductRequest",
  },
  restriction: {
    api: historicalRestrictionApi,
    className: "HistoricalRestrictionApi",
    createKey: "restrictionRequest",
    patchKey: "patchedRestrictionRequest",
  },
  icpe: {
    api: historicalICPEApi,
    className: "HistoricalICPEApi",
    createKey: "iCPERequest",
    patchKey: "patchedICPERequest",
  },
  bombings: {
    api: historicalBombingsApi,
    className: "HistoricalBombingsApi",
    createKey: "bombingsRequest",
    patchKey: "patchedBombingsRequest",
  },
};

const HISTORICAL_CONFIG = {
  ACCIDENTS: HISTORICAL.accident,
  ACTIVITIES: HISTORICAL.activity,
  KNOWN_POLLUTION_SITES: HISTORICAL.pollution,
  PRODUCTS: HISTORICAL.product,
  USAGE_RESTRICTIONS: HISTORICAL.restriction,
  ICPE_CLASSIFICATIONS: HISTORICAL.icpe,
  BOMBINGS: HISTORICAL.bombings,
  ACCIDENT: HISTORICAL.accident,
  ACTIVITY: HISTORICAL.activity,
  POLLUTION: HISTORICAL.pollution,
  PRODUCT: HISTORICAL.product,
  RESTRICTION: HISTORICAL.restriction,
  ICPE: HISTORICAL.icpe,
};

const models = {
  ACCIDENTS: Accident,
  ACTIVITIES: Activity,
  KNOWN_POLLUTION_SITES: Pollution,
  PRODUCTS: Product,
  USAGE_RESTRICTIONS: Restriction,
  ICPE_CLASSIFICATIONS: ICPE,
  BOMBINGS: Bombings,

  ACCIDENT: Accident,
  ACTIVITY: Activity,
  POLLUTION: Pollution,
  PRODUCT: Product,
  RESTRICTION: Restriction,
  ICPE: ICPE,
};

const TestimonyRepo = useRepo(Testimony);

export const useTestimonialStore = defineStore("testimonial", {
  state: () => ({
    storedTestimony: TestimonyRepo.make({ uuid: "" }),
  }),

  actions: {
    async fetchItems({ type, metadata }) {
      const cfg = HISTORICAL_CONFIG[type];
      const ModelRepo = useRepo(models[type]);
      const { results: items, count } = await cfg.api.list(metadataToParams(metadata));

      if (!items.length) {
        console.log(`No ${type.toLowerCase()} found`);
        return null;
      }

      ModelRepo.save(items);
      return {
        items,
        count,
      };
    },

    async createOrUpdateHistoricalItem({ type, item }) {
      const cfg = HISTORICAL_CONFIG[type];
      const ModelRepo = useRepo(models[type]);
      const filteredItem = _.omit(item, excludeFields);
      let saved = null;

      if (!filteredItem.uuid || !isUuid(filteredItem.uuid)) {
        saved = await cfg.api.create({ [cfg.createKey]: filteredItem });
      } else {
        saved = await cfg.api.partialUpdate({
          uuid: filteredItem.uuid,
          [cfg.patchKey]: filteredItem,
        });
      }
      if (saved) {
        ModelRepo.save(saved);
        return saved;
      }
    },

    async deleteItem({ type, uuid }) {
      const cfg = HISTORICAL_CONFIG[type];
      const ModelRepo = useRepo(models[type]);

      await cfg.api.destroy({ uuid });
      return ModelRepo.destroy(uuid);
    },

    async fetchTestimonies(queryAsObject = {}) {
      const missionStore = useMissionStore();
      const TestimonyRepo = useRepo(Testimony);
      const { results: testimonies, count } = await testimonyApi.list({
        project: missionStore.currentMissionUuid,
        ...filtersToParams(queryAsObject.filters),
        pageSize: queryAsObject.itemsPerPage || 10,
        page: queryAsObject.page || 1,
      });

      if (!testimonies.length) {
        console.log("No testimony found");
        return null;
      }
      TestimonyRepo.save(testimonies);
      return { testimonies, count };
    },

    async createOrUpdateCurrentTestimony(testimony) {
      const missionStore = useMissionStore();
      const TestimonyRepo = useRepo(Testimony);
      testimony.project = missionStore.currentMissionUuid;
      let newTestimony = null;
      const filteredTestimony = _.omit(testimony, excludeFields);

      if (!filteredTestimony.uuid || !isUuid(filteredTestimony.uuid)) {
        try {
          newTestimony = await testimonyApi.create({
            testimonyRequest: filteredTestimony,
          });
        } catch (error) {
          console.error("Error creating testimony:", error);
          throw new Error("Failed to create testimony");
        }
        if (newTestimony) {
          TestimonyRepo.save(newTestimony);
        }
      } else {
        newTestimony = await testimonyApi.partialUpdate({
          uuid: filteredTestimony.uuid,
          patchedTestimonyRequest: filteredTestimony,
        });
        if (newTestimony) {
          TestimonyRepo.save(newTestimony);
        }
      }
      this.setStoredTestimony(newTestimony);
      return newTestimony;
    },

    async deleteCurrentTestimony(uuid) {
      const TestimonyRepo = useRepo(Testimony);
      await testimonyApi.destroy({ uuid });
      return TestimonyRepo.destroy(uuid);
    },

    async fetchTestimonyRemarkableElements({ filters = {}, pagination = {} }) {
      const pageSize = pagination.pageSize || 10;
      const page = pagination.page || 1;
      const listParams = { ...filters, pageSize: 100 };
      const markers = await Promise.all([
        zoneApi.list(listParams),
        componentApi.list(listParams),
        buildingApi.list(listParams),
        outdoorSpaceApi.list(listParams),
        basiasBasolSisNearbySiteOtherApi.list(listParams),
        spreadingApi.list(listParams),
      ]);
      const results = _.flatten(markers.map((el) => el.results));
      const totalItems = results.length;
      // Apply pagination manually
      const paginatedResults = results.slice((page - 1) * pageSize, page * pageSize);

      if (!paginatedResults.length) {
        console.log("No remarkable element found");
        return null;
      }
      return {
        items: paginatedResults,
        totalItems: totalItems,
      };
    },

    async fetchItem({ type, uuid }) {
      const cfg = HISTORICAL_CONFIG[type];
      const ModelRepo = useRepo(models[type]);
      const item = await cfg.api.retrieve({ uuid });
      ModelRepo.save(item);
      return item;
    },

    async fetchAllItems({ type, filters }) {
      const cfg = HISTORICAL_CONFIG[type];
      const ModelRepo = useRepo(models[type]);
      ModelRepo.flush();
      const response = await cfg.api.list({
        pageSize: 500,
        ...filtersToParams(filters),
      });
      return ModelRepo.save(response.results);
    },

    setStoredTestimony(testimony) {
      this.storedTestimony = testimony;
    },
    async partialUpdate({ data, fields }) {
      const cfg = HISTORICAL_CONFIG[data.typology];
      // Preserve the gRPC field-mask intent: only send the listed fields.
      const body =
        Array.isArray(fields) && fields.length
          ? fields.reduce((acc, field) => ({ ...acc, [field]: data[field] }), {
              uuid: data.uuid,
            })
          : data;
      const response = await cfg.api.partialUpdate({
        uuid: data.uuid,
        [cfg.patchKey]: body,
      });
      const ModelRepo = useRepo(models[data.typology]);
      return ModelRepo.save(response);
    },

    resetState() {
      this.storedTestimony = TestimonyRepo.make({ uuid: "" });
    },
  },

  getters: {
    getHistoricalItems: () => {
      const modelsSet = new Set(Object.values(models));
      const historicalItems = [];
      for (const model of modelsSet) {
        const ModelRepo = useRepo(model);
        historicalItems.push(...ModelRepo.all());
      }
      return historicalItems;
    },
  },
});
